import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Upload, X, Check, PenTool, Type, Image } from 'lucide-react'
import { XMarkIcon as HeroXMark } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import Button from './Button'
import SignatureCanvas from './SignatureCanvas'

export interface SignatureDialogProps {
  isOpen: boolean
  onClose: () => void
  onSave: (signature: string, name: string, comment?: string, stampImage?: string) => void
  title?: string
  initialSignature?: string | null
  initialStampImage?: string | null
  initialName?: string
  initialComment?: string
}

export default function SignatureDialog({
  isOpen,
  onClose,
  onSave,
  title = 'Add Signature',
  initialSignature = null,
  initialStampImage = null,
  initialName = '',
  initialComment = ''
}: SignatureDialogProps) {
  const [activeTab, setActiveTab] = useState<'draw' | 'upload' | 'text'>('draw')
  const [signature, setSignature] = useState<string | null>(initialSignature)
  const [name, setName] = useState(initialName)
  const [comment] = useState(initialComment)
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [stampImage, setStampImage] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const stampImageInputRef = useRef<HTMLInputElement>(null)
  const hasOpenedThisSessionRef = useRef(false)

  useEffect(() => {
    if (isOpen) setName(initialName)
  }, [isOpen, initialName])

  useEffect(() => {
    if (!isOpen) {
      hasOpenedThisSessionRef.current = false
      return
    }
    if (hasOpenedThisSessionRef.current) return
    hasOpenedThisSessionRef.current = true
    const sig = initialSignature ?? null
    setSignature(sig)
    setUploadedImage(sig)
    const stamp =
      initialStampImage && String(initialStampImage).trim() ? initialStampImage : null
    setStampImage(stamp)
    setName(initialName)
    setActiveTab('draw')
    setIsDragOver(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (stampImageInputRef.current) stampImageInputRef.current.value = ''
  }, [isOpen, initialSignature, initialStampImage, initialName])

  useEffect(() => {
    if (!isOpen) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const processFile = (file: File) => {
    setIsUploading(true)
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file')
      setIsUploading(false)
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB')
      setIsUploading(false)
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => {
      const result = e.target?.result as string
      setUploadedImage(result)
      setSignature(result)
      setIsUploading(false)
    }
    reader.onerror = () => {
      alert('Error reading file')
      setIsUploading(false)
    }
    reader.readAsDataURL(file)
  }

  const processStampImage = (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB')
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => {
      setStampImage(e.target?.result as string)
    }
    reader.onerror = () => alert('Error reading file')
    reader.readAsDataURL(file)
  }

  const handleSave = () => {
    if (!signature || !name.trim()) {
      alert('Please provide both signature and name')
      return
    }
    onSave(signature, name.trim(), comment.trim() || undefined, stampImage || undefined)
    handleClose()
  }

  const handleClose = () => {
    setSignature(initialSignature)
    setName(initialName)
    setUploadedImage(initialSignature)
    setStampImage(null)
    setIsDragOver(false)
    setActiveTab('draw')
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (stampImageInputRef.current) stampImageInputRef.current.value = ''
    onClose()
  }

  const generateTextSignature = (text: string): string => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return ''
    canvas.width = 400
    canvas.height = 120
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.font = 'italic 32px "Brush Script MT", "Lucida Handwriting", cursive'
    ctx.fillStyle = '#000000'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, canvas.width / 2, canvas.height / 2)
    return canvas.toDataURL('image/png')
  }

  const handleNameChange = (newName: string) => {
    setName(newName)
    if (activeTab === 'text') {
      setSignature(newName.trim() ? generateTextSignature(newName.trim()) : null)
    }
  }

  const handleTabChange = (tab: 'draw' | 'upload' | 'text') => {
    setActiveTab(tab)
    if (tab === 'text' && name.trim()) {
      setSignature(generateTextSignature(name.trim()))
    } else if (tab === 'draw') {
      setSignature(null)
      setUploadedImage(null)
    }
  }

  if (!isOpen) return null

  const isSaveDisabled = !signature || !name.trim()

  const content = (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signature-dialog-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <h2 id="signature-dialog-title" className="text-lg font-semibold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            onClick={handleClose}
            aria-label="Close"
          >
            <HeroXMark className="h-5 w-5" />
          </button>
        </header>

        <div className="space-y-6 px-5 py-4">
          <div className="space-y-2">
            <label htmlFor="signature-name" className="block text-sm font-medium text-gray-700">
              Name *
            </label>
            <input
              id="signature-name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Enter your name"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
            />
          </div>

          <div className="flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1">
            {(
              [
                { id: 'draw' as const, label: 'Draw', icon: PenTool },
                { id: 'text' as const, label: 'Text', icon: Type },
                { id: 'upload' as const, label: 'Upload', icon: Image }
              ] as const
            ).map(({ id, label, icon: Icon }) => (
              <Button
                key={id}
                type="button"
                variant={activeTab === id ? 'primary' : 'secondary'}
                onClick={() => handleTabChange(id)}
                className="flex flex-1 items-center justify-center gap-2 px-3 py-2 text-sm"
              >
                <Icon className="h-4 w-4" />
                {label}
              </Button>
            ))}
            <Button
              type="button"
              variant="secondary"
              onClick={() => stampImageInputRef.current?.click()}
              className="flex flex-1 items-center justify-center gap-2 px-3 py-2 text-sm"
            >
              <Image className="h-4 w-4" />
              Stamp
            </Button>
            <input
              ref={stampImageInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) processStampImage(file)
              }}
              className="hidden"
              aria-label="Upload stamped image"
            />
          </div>

          {activeTab === 'draw' ? (
            <SignatureCanvas
              width={500}
              height={200}
              onSignatureChange={setSignature}
              initialSignature={null}
              className="w-full"
            />
          ) : activeTab === 'text' ? (
            <div className="space-y-4 text-center">
              <p className="text-sm text-gray-600">Enter your name above to generate a styled signature</p>
              {name.trim() && signature ? (
                <div className="rounded-lg border bg-gray-50 p-4">
                  <img
                    src={signature}
                    alt="Text signature preview"
                    className="mx-auto max-h-20 max-w-full"
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <div className="space-y-4">
              <div
                className={clsx(
                  'rounded-lg border-2 border-dashed p-4 text-center transition-colors',
                  isDragOver ? 'bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                )}
                onDragOver={(e) => {
                  e.preventDefault()
                  setIsDragOver(true)
                }}
                onDragLeave={(e) => {
                  e.preventDefault()
                  setIsDragOver(false)
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  setIsDragOver(false)
                  const file = e.dataTransfer.files?.[0]
                  if (file) processFile(file)
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) processFile(file)
                  }}
                  className="hidden"
                  id="signature-upload"
                />
                <label htmlFor="signature-upload" className="flex cursor-pointer flex-col items-center space-y-2">
                  <Upload className={clsx('h-8 w-8', isDragOver ? 'text-blue-500' : 'text-gray-400')} />
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-brand-red-600 hover:underline">Click to upload</span>
                    {' '}or drag and drop
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                </label>
              </div>
              {isUploading ? (
                <div className="py-4 text-center">
                  <div className="inline-block h-6 w-6 animate-spin rounded-full border-b-2 border-brand-red-600" />
                  <p className="mt-2 text-sm text-gray-600">Uploading...</p>
                </div>
              ) : null}
              {uploadedImage ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Preview</p>
                  <div className="rounded-lg border bg-gray-50 p-2">
                    <img src={uploadedImage} alt="Uploaded signature" className="mx-auto max-h-24 max-w-full" />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setUploadedImage(null)
                      setSignature(null)
                      if (fileInputRef.current) fileInputRef.current.value = ''
                    }}
                    className="flex w-full items-center justify-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Remove Image
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {signature ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Signature Preview</p>
              <div className="rounded-lg border bg-gray-50 p-2">
                <img src={signature} alt="Signature preview" className="mx-auto max-h-20 max-w-full" />
              </div>
            </div>
          ) : null}

          {stampImage ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Stamp Image Preview</p>
              <div className="rounded-lg border bg-gray-50 p-2">
                <img src={stampImage} alt="Stamp preview" className="mx-auto max-h-20 max-w-full" />
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setStampImage(null)
                  if (stampImageInputRef.current) stampImageInputRef.current.value = ''
                }}
                className="flex w-full items-center justify-center gap-2"
              >
                <X className="h-4 w-4" />
                Remove Stamp Image
              </Button>
            </div>
          ) : null}
        </div>

        <footer className="flex flex-col gap-2 border-t border-gray-100 px-5 py-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={handleClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            disabled={isSaveDisabled}
            className="flex w-full items-center justify-center gap-2 sm:w-auto"
          >
            <Check className="h-4 w-4" />
            Save Signature
          </Button>
        </footer>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
